import React from 'react';
import ReactApexChart from 'react-apexcharts';

const formatCount = (value) => Math.round(value).toLocaleString();

const AppVersionStatistics = ({audience, versions}) => {
    const title = `${audience.key === 'users' ? 'Users' : 'Drivers'} by app version`;
    const versionLabels = versions.map(({version}) => version ?? 'Unknown');
    const largestCount = versions.reduce((largest, {android, ios}) => Math.max(largest, android, ios), 0);
    const tickAmount = Math.min(5, Math.max(1, largestCount));
    const options = {
        chart: {
            type: 'bar',
            toolbar: {show: false},
            fontFamily: 'inherit',
        },
        colors: ['#218739', '#3867d6'],
        plotOptions: {bar: {horizontal: true, barHeight: '65%', borderRadius: 3}},
        dataLabels: {enabled: true, formatter: formatCount},
        xaxis: {
            categories: versionLabels,
            min: 0,
            max: Math.ceil(Math.max(1, largestCount) / tickAmount) * tickAmount,
            tickAmount,
            decimalsInFloat: 0,
            title: {text: `Unique ${audience.label}`},
            labels: {formatter: formatCount},
        },
        yaxis: {labels: {maxWidth: 140}},
        legend: {position: 'top', horizontalAlign: 'left'},
        tooltip: {y: {formatter: (value) => `${formatCount(value)} ${audience.label}`}},
    };
    const series = [
        {name: 'Android', data: versions.map(({android}) => android)},
        {name: 'iOS', data: versions.map(({ios}) => ios)},
    ];

    return (
        <section className="card h-100" aria-labelledby={`${audience.key}-versions-title`}>
            <div className="card-body">
                <h4 id={`${audience.key}-versions-title`} className="h5 mb-3">{title}</h4>
                {versions.length === 0 ? (
                    <p className="text-center text-muted py-5 mb-0">
                        No app version data available for {audience.label} yet.
                    </p>
                ) : (
                    <>
                        <div
                            style={{maxHeight: 440, overflowY: versions.length > 5 ? 'scroll' : 'auto', overflowX: 'hidden'}}
                            tabIndex={versions.length > 5 ? 0 : undefined}
                            role="region"
                            aria-label={`${title} chart`}
                        >
                            <div>
                                <ReactApexChart
                                    options={options}
                                    series={series}
                                    type="bar"
                                    height={Math.max(260, versions.length * 64 + 100)}
                                />
                            </div>
                        </div>
                        <div className="table-responsive mt-3" style={{maxHeight: 320, overflowY: 'auto'}}>
                            <table className="table table-sm table-striped mb-0">
                                <caption className="sr-only">{title}</caption>
                                <thead>
                                <tr>
                                    <th scope="col">App version</th>
                                    <th scope="col" className="text-right">Android {audience.label}</th>
                                    <th scope="col" className="text-right">iOS {audience.label}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {versions.map((version, index) => (
                                    <tr key={JSON.stringify(version.version)}>
                                        <th scope="row" className="font-weight-normal" style={{overflowWrap: 'anywhere'}}>
                                            {versionLabels[index]}
                                        </th>
                                        <td className="text-right">{formatCount(version.android)}</td>
                                        <td className="text-right">{formatCount(version.ios)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default AppVersionStatistics;
